/**
 * Status + live indicator tokens — canonical classes for pills, badges, countdowns.
 * Live styling matches production LiveGamePeriodBadge (emerald accent).
 */
import { semanticColors, domainColors } from './colors';
import { typography } from './typography';
import { radii } from './radii';
import { motion } from './motion';

const { accent, warning, danger, text } = semanticColors;

export const statusColors = {
  live: {
    dot: 'bg-emerald-400',
    dotGlow: 'shadow-[0_0_8px_rgba(52,211,153,0.85)]',
    text: accent.text,
    surface: accent.surfaceEmphasis,
    border: accent.borderEmphasis,
    pulse: motion.livePulse,
  },
  upcoming: {
    text: 'text-amber-300/90',
    surface: warning.surfaceEmphasis,
    border: warning.borderEmphasis,
  },
  scheduled: {
    text: text.secondary,
    surface: 'bg-black/35',
    border: 'border-gray-700',
  },
  final: {
    text: text.muted,
    surface: 'bg-gray-900/50',
    border: 'border-gray-800',
  },
  success: {
    text: accent.text,
    surface: accent.surface,
    border: accent.border,
  },
  danger: {
    text: danger.text,
    surface: danger.surface,
    border: danger.border,
  },
  wallet: domainColors.wallet,
};

/** Compact LIVE pill (dot + label) — use LiveIndicator component */
export const liveIndicator = {
  root: 'inline-flex items-center gap-1.5 shrink-0',
  dot: `h-2 w-2 rounded-full ${statusColors.live.dot} ${statusColors.live.dotGlow} ${statusColors.live.pulse}`,
  dotSm: `h-1.5 w-1.5 rounded-full ${statusColors.live.dot} ${statusColors.live.pulse}`,
  label: `${typography.micro} ${statusColors.live.text}`,
};

/** Period/score chip beside live dot */
export const livePeriodChip = {
  base: `${typography.stat} px-2.5 py-1 ${radii.control} ${statusColors.live.text} ${statusColors.live.surface} border ${statusColors.live.border} shadow-[0_0_14px_rgba(16,185,129,0.2)] uppercase tracking-wide`,
};

/** Countdown / kickoff time display */
export const countdown = {
  label: `${typography.micro} ${text.muted}`,
  value: `${typography.stat} ${text.primary}`,
  urgent: `${typography.stat} text-amber-300 tabular-nums`,
};

/** Generic status pill scaffold */
export const statusPill = {
  base: `inline-flex items-center gap-1.5 border px-2.5 py-1 ${radii.pill} ${typography.micro}`,
  live: `${statusColors.live.surface} ${statusColors.live.border} ${statusColors.live.text}`,
  upcoming: `${statusColors.upcoming.surface} ${statusColors.upcoming.border} ${statusColors.upcoming.text}`,
  scheduled: `${statusColors.scheduled.surface} ${statusColors.scheduled.border} ${statusColors.scheduled.text}`,
  final: `${statusColors.final.surface} ${statusColors.final.border} ${statusColors.final.text}`,
};
